/**
 * One-time secret reveal — a shared dialog for showing a generated secret
 * (a new PIN, a temporary password) exactly once. The secret lives in the
 * caller's state and is wiped on close, so this dialog cannot recover it.
 *
 * Три места на `/people` делят один каркас: заголовок + подзаголовок +
 * caution-плашка «показан один раз» + моноширинное значение + «Скопировать» /
 * «Готово». Различия — только данные (`display="pin"` даёт разрядку цифр,
 * `"mono"` — обычный моноширинный пароль) и `testIdPrefix`.
 *
 * See docs/frontend/patterns.md §Dialog — «Разовый показ секрета».
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";

interface SecretRevealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Identity / context line under the title (muted). */
  subtitle?: React.ReactNode;
  /** Extra mono line between subtitle and secret (e.g. the created user's email). */
  note?: React.ReactNode;
  /** Amber "shown once" warning. Omit → no caution plate. */
  cautionText?: string;
  /** The secret to reveal. Rendered only while non-null. */
  secret: string | null;
  /** `"pin"` spaces out digits big-and-centered; `"mono"` is a plain mono string. */
  display?: "mono" | "pin";
  copyLabel: string;
  copiedLabel: string;
  doneLabel: string;
  /** Drives `${prefix}-dialog` / `-value` / `-copy` / `-close` test ids. */
  testIdPrefix: string;
}

export function SecretRevealDialog({
  open,
  onOpenChange,
  title,
  subtitle,
  note,
  cautionText,
  secret,
  display = "mono",
  copyLabel,
  copiedLabel,
  doneLabel,
  testIdPrefix,
}: SecretRevealDialogProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  useReleaseDirtyState(open && secret !== null);

  // Reset the copied flag on every close path — the next reveal starts fresh
  // (the caller reopens with a new secret).
  function handleOpenChange(next: boolean): void {
    if (!next) setCopied(false);
    onOpenChange(next);
  }

  async function copy(): Promise<void> {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      // Clipboard may be unavailable in tests / iframes; ignore.
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {subtitle && <span className="text-muted-foreground block text-sm">{subtitle}</span>}
        {note && <span className="block font-mono text-xs">{note}</span>}
        {cautionText && (
          <div className="tone-caution rounded-md px-3 py-2 text-xs" role="alert">
            {cautionText}
          </div>
        )}
        <div
          className={cn(
            "bg-muted rounded-md font-mono",
            display === "pin"
              ? "p-4 text-center text-3xl tracking-[0.4em] tabular-nums"
              : "p-3 text-sm break-all",
          )}
          data-testid={`${testIdPrefix}-value`}
        >
          {secret}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            data-testid={`${testIdPrefix}-copy`}
            onClick={() => void copy()}
          >
            {copied ? copiedLabel : copyLabel}
          </Button>
          <Button data-testid={`${testIdPrefix}-close`} onClick={() => handleOpenChange(false)}>
            {doneLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
